import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Activity, Database, Search } from 'lucide-react';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

function JsonViewer({ data, testId }) {
  return (
    <ScrollArea className="h-64 w-full rounded-lg bg-[#0b1120] text-[#e5e7eb] p-3 text-xs" data-testid={testId}>
      <pre className="whitespace-pre-wrap break-words font-mono">{data}</pre>
    </ScrollArea>
  );
}

function DevApiPage() {
  const [health, setHealth] = useState(null);
  const [healthError, setHealthError] = useState('');

  const [services, setServices] = useState(null);
  const [servicesError, setServicesError] = useState('');

  const [serviceId, setServiceId] = useState('althrif');
  const [vehicleSize, setVehicleSize] = useState('folksbill');
  const [pricing, setPricing] = useState(null);
  const [pricingError, setPricingError] = useState('');

  const [loading, setLoading] = useState({
    health: false,
    services: false,
    pricing: false,
  });

  const handleFetchHealth = async () => {
    setLoading(prev => ({ ...prev, health: true }));
    setHealthError('');
    try {
      const res = await fetch(`${backendUrl}/api/health`);
      const json = await res.json();
      setHealth(json);
    } catch (e) {
      setHealth(null);
      setHealthError(e.message || 'Villa við að sækja /api/health');
    } finally {
      setLoading(prev => ({ ...prev, health: false }));
    }
  };

  const handleFetchServices = async () => {
    setLoading(prev => ({ ...prev, services: true }));
    setServicesError('');
    try {
      const res = await fetch(`${backendUrl}/api/services`);
      const json = await res.json();
      setServices(json);
    } catch (e) {
      setServices(null);
      setServicesError(e.message || 'Villa við að sækja /api/services');
    } finally {
      setLoading(prev => ({ ...prev, services: false }));
    }
  };

  const handleFetchPricing = async () => {
    setLoading(prev => ({ ...prev, pricing: true }));
    setPricingError('');
    try {
      const res = await fetch(
        `${backendUrl}/api/services/${serviceId}/pricing?vehicle_size=${encodeURIComponent(
          vehicleSize
        )}`
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Villa ${res.status}`);
      }
      const json = await res.json();
      setPricing(json);
    } catch (e) {
      setPricing(null);
      setPricingError(e.message || 'Villa við að sækja pricing');
    } finally {
      setLoading(prev => ({ ...prev, pricing: false }));
    }
  };

  const pretty = (obj) => (obj ? JSON.stringify(obj, null, 2) : '');

  return (
    <div className="min-h-screen py-12" data-testid="dev-api-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10">
          <h1
            className="text-3xl md:text-4xl font-bold text-[#2C3E50] mb-2"
            style={{ fontFamily: 'Work Sans, sans-serif' }}
          >
            Dev / API Yfirlit
          </h1>
          <p className="text-sm md:text-base text-[#8B9BA8] max-w-2xl">
            Þessi síða hjálpar til við að staðfesta að portal‑backend sé í sambandi við CRM. Hún er aðeins
            ætluð þróun/prófun og er ekki sýnileg í aðalvalmynd.
          </p>
        </div>

        <Tabs defaultValue="health" className="w-full" data-testid="dev-api-tabs">
          <TabsList className="grid grid-cols-3 w-full mb-6">
            <TabsTrigger value="health" data-testid="dev-tab-health">
              <Activity className="h-4 w-4 mr-2" />
              Health
            </TabsTrigger>
            <TabsTrigger value="services" data-testid="dev-tab-services">
              <Database className="h-4 w-4 mr-2" />
              Services
            </TabsTrigger>
            <TabsTrigger value="pricing" data-testid="dev-tab-pricing">
              <Search className="h-4 w-4 mr-2" />
              Pricing
            </TabsTrigger>
          </TabsList>

          {/* Health */}
          <TabsContent value="health">
            <Card className="border-[#E8ECEF] shadow-lg" data-testid="dev-health-card">
              <CardHeader>
                <CardTitle className="text-lg text-[#2C3E50]">/api/health</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button
                  onClick={handleFetchHealth}
                  className="bg-[#0A4D8C] hover:bg-[#083A6B] text-white"
                  disabled={loading.health}
                  data-testid="dev-health-fetch"
                >
                  {loading.health ? 'Sæki...' : 'Sækja health'}
                </Button>

                {healthError && (
                  <p className="text-sm text-[#EF4444]" data-testid="dev-health-error">
                    {healthError}
                  </p>
                )}

                {health && (
                  <div className="space-y-2" data-testid="dev-health-summary">
                    <p className="text-sm text-[#2C3E50]">
                      <span className="font-semibold">Status:</span> {health.status}
                    </p>
                    <p className="text-sm text-[#2C3E50]">
                      <span className="font-semibold">CRM tenging:</span>{' '}
                      {health.crm_connected ? '✓ virkt' : '✗ óvirkt'}
                    </p>
                    <JsonViewer data={pretty(health)} testId="dev-health-json" />
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Services */}
          <TabsContent value="services">
            <Card className="border-[#E8ECEF] shadow-lg" data-testid="dev-services-card">
              <CardHeader>
                <CardTitle className="text-lg text-[#2C3E50]">/api/services</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button
                  onClick={handleFetchServices}
                  className="bg-[#0A4D8C] hover:bg-[#083A6B] text-white"
                  disabled={loading.services}
                  data-testid="dev-services-fetch"
                >
                  {loading.services ? 'Sæki...' : 'Sækja services'}
                </Button>

                {servicesError && (
                  <p className="text-sm text-[#EF4444]" data-testid="dev-services-error">
                    {servicesError}
                  </p>
                )}

                {Array.isArray(services) && services.length > 0 && (
                  <div className="space-y-4">
                    <div className="text-sm text-[#2C3E50]" data-testid="dev-services-summary">
                      <p className="font-semibold mb-1">Yfirlit þjónusta:</p>
                      <ul className="list-disc ml-5 space-y-1">
                        {services.map((s) => (
                          <li key={s.id}>
                            <span className="font-medium">{s.name}</span>{' '}
                            <span className="text-[#8B9BA8]">({s.id})</span>
                            {s.prices && Object.keys(s.prices || {}).length > 0 && (
                              <span className="ml-2 text-xs text-[#10B981]">með price_map</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <JsonViewer data={pretty(services)} testId="dev-services-json" />
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pricing */}
          <TabsContent value="pricing">
            <Card className="border-[#E8ECEF] shadow-lg" data-testid="dev-pricing-card">
              <CardHeader>
                <CardTitle className="text-lg text-[#2C3E50]">/api/services/:id/pricing</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="dev-service-id" className="text-[#2C3E50]">
                      service_id
                    </Label>
                    <Input
                      id="dev-service-id"
                      value={serviceId}
                      onChange={(e) => setServiceId(e.target.value)}
                      className="mt-2"
                      data-testid="dev-pricing-service-id"
                    />
                  </div>
                  <div>
                    <Label htmlFor="dev-vehicle-size" className="text-[#2C3E50]">
                      vehicle_size
                    </Label>
                    <Input
                      id="dev-vehicle-size"
                      value={vehicleSize}
                      onChange={(e) => setVehicleSize(e.target.value)}
                      className="mt-2"
                      data-testid="dev-pricing-vehicle-size"
                    />
                  </div>
                </div>

                <Button
                  onClick={handleFetchPricing}
                  className="bg-[#0A4D8C] hover:bg-[#083A6B] text-white"
                  disabled={loading.pricing}
                  data-testid="dev-pricing-fetch"
                >
                  {loading.pricing ? 'Sæki...' : 'Sækja pricing'}
                </Button>

                {pricingError && (
                  <p className="text-sm text-[#EF4444]" data-testid="dev-pricing-error">
                    {pricingError}
                  </p>
                )}

                {pricing && (
                  <div className="space-y-2" data-testid="dev-pricing-summary">
                    <p className="text-sm text-[#2C3E50]">
                      <span className="font-semibold">Verð:</span>{' '}
                      {pricing.price ? `${pricing.price.toLocaleString()} kr.` : '–'}
                    </p>
                    <JsonViewer data={pretty(pricing)} testId="dev-pricing-json" />
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

export default DevApiPage;
